import { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';

const ACOES = [
    { valor: 'concluir', nome: 'Concluir' },
    { valor: 'reagendar', nome: 'Reagendar' },
    { valor: 'ignorar', nome: 'Ignorar' },
];

const PLACEHOLDER_NOME = {
    concluir: 'Quem está concluindo?',
    reagendar: 'Quem está reagendando?',
    ignorar: 'Quem está ignorando?',
};

const BOTAO_CONFIRMAR = {
    concluir: { texto: 'Confirmar conclusão', cor: 'bg-green-700 hover:bg-green-600' },
    reagendar: { texto: 'Confirmar reagendamento', cor: 'bg-blue-700 hover:bg-blue-600' },
    ignorar: { texto: 'Confirmar ignorar', cor: 'bg-red-700 hover:bg-red-600' },
};

const dataIso = (data) => data.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

const amanhaIso = () => {
    const data = new Date();
    data.setDate(data.getDate() + 1);
    return dataIso(data);
};

export default function FormConclusaoTarefa({ tarefa, enviando, onConfirmar, onCancelar, compacto = false }) {
    const [acao, setAcao] = useState('concluir');
    const [executadoPor, setExecutadoPor] = useState('');
    const [observacao, setObservacao] = useState('');
    const [novaData, setNovaData] = useState('');
    const [erroValidacao, setErroValidacao] = useState('');

    const acoes = tarefa.periodicidade === 'Diaria' ? ACOES.filter((a) => a.valor !== 'reagendar') : ACOES;
    const observacaoObrigatoria = acao !== 'concluir';
    const dataMinima = amanhaIso();
    const dataMaxima = tarefa.periodoFim ?? undefined;
    const semDataDisponivel = acao === 'reagendar' && dataMaxima != null && dataMaxima < dataMinima;
    const botao = BOTAO_CONFIRMAR[acao];

    const tamanhoTexto = compacto ? 'text-sm' : '';
    const classeCampo = `bg-gray-800 text-white border border-gray-700 rounded px-3 py-1.5 focus:outline-none focus:border-accent disabled:opacity-50 ${tamanhoTexto}`;

    const fecharComEsc = (e) => e.key === 'Escape' && onCancelar();

    const trocarAcao = (valor) => {
        setAcao(valor);
        setErroValidacao('');
    };

    const validar = () => {
        if (!executadoPor.trim()) return 'Informe o seu nome.';
        if (acao === 'reagendar') {
            if (semDataDisponivel) return 'Não há data disponível para reagendar dentro do período desta tarefa.';
            if (!novaData) return 'Escolha a nova data.';
            if (novaData < dataMinima || (dataMaxima && novaData > dataMaxima)) return 'A nova data deve estar dentro do período da tarefa.';
        }
        if (observacaoObrigatoria && !observacao.trim()) return 'Informe o motivo.';
        return '';
    };

    const confirmar = (e) => {
        e.preventDefault();
        const erro = validar();
        setErroValidacao(erro);
        if (erro) return;
        onConfirmar(acao, {
            executadoPor: executadoPor.trim(),
            observacao: observacao.trim(),
            novaData: acao === 'reagendar' ? novaData : undefined,
        });
    };

    return (
        <form className="flex flex-col gap-2 mt-2" onSubmit={confirmar}>
            <div className="flex gap-1">
                {acoes.map((a) => (
                    <button
                        key={a.valor}
                        type="button"
                        disabled={enviando}
                        onClick={() => trocarAcao(a.valor)}
                        className={`px-3 py-1 rounded border ${tamanhoTexto} ${acao === a.valor
                            ? 'bg-accent/30 border-accent text-white'
                            : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}
                    >
                        {a.nome}
                    </button>
                ))}
            </div>
            <input
                type="text"
                autoFocus
                maxLength={150}
                placeholder={PLACEHOLDER_NOME[acao]}
                value={executadoPor}
                disabled={enviando}
                onChange={(e) => setExecutadoPor(e.target.value)}
                onKeyDown={fecharComEsc}
                className={`w-full max-w-sm ${classeCampo}`}
            />
            {acao === 'reagendar' && (
                semDataDisponivel ? (
                    <p className={`text-yellow-400 ${tamanhoTexto}`}>Não há data disponível para reagendar dentro do período desta tarefa.</p>
                ) : (
                    <label className={`flex items-center gap-2 text-gray-300 ${tamanhoTexto}`}>
                        Nova data
                        <input
                            type="date"
                            min={dataMinima}
                            max={dataMaxima}
                            value={novaData}
                            disabled={enviando}
                            onChange={(e) => setNovaData(e.target.value)}
                            onKeyDown={fecharComEsc}
                            className={`[color-scheme:dark] ${classeCampo}`}
                        />
                    </label>
                )
            )}
            <textarea
                rows={2}
                maxLength={500}
                placeholder={observacaoObrigatoria ? 'Motivo (obrigatório)' : 'Observação (opcional)'}
                value={observacao}
                disabled={enviando}
                onChange={(e) => setObservacao(e.target.value)}
                onKeyDown={fecharComEsc}
                className={`w-full max-w-md resize-y ${classeCampo}`}
            />
            {erroValidacao && <p className={`text-red-400 ${tamanhoTexto}`}>{erroValidacao}</p>}
            <div className="flex items-center gap-2">
                <button
                    type="submit"
                    disabled={enviando}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-white disabled:opacity-50 ${botao.cor} ${tamanhoTexto}`}
                >
                    {enviando && <FaSpinner className="animate-spin" />}
                    {botao.texto}
                </button>
                <button
                    type="button"
                    onClick={onCancelar}
                    disabled={enviando}
                    className={`px-3 py-1.5 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50 ${tamanhoTexto}`}
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
}
